import { Component, OnInit } from '@angular/core';
import { Input, forwardRef } from '@angular/core';
import { Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, } from "@angular/forms";

/**
 * UppSearchComponent provides a reusable search bar component that
 * implements ControlValueAccessor for form integration.
 * 
 * This component can be used in both template-driven and reactive forms,
 * and provides customization options for placeholder text, color, and disabled state.
 *
 * @example
 * Basic usage:
 * <upp-searchbar placeholder="Search..." (Changed)="onSearchChange($event)"></upp-searchbar>
 *
 * @example
 * Reactive form usage:
 * <upp-searchbar formControlName="searchTerm" [disabled]="isDisabled"></upp-searchbar>
 */
@Component({
    selector: 'upp-search',
    templateUrl:'./upp-search.html',
    styleUrls: [ 
        './upp-search.scss'
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UppSearchComponent),
            multi: true
        }            
    ]
})
export class UppSearchComponent implements ControlValueAccessor, OnInit {
    /** Placeholder text for the input. */
    @Input() placeholder = '';
    /** Color for this item */
    @Input() color = 'default';
    /** Disabled status for the text entry */
    @Input() disabled = false;
    /** Enables od disabled the kiosk input */
    @Input() kiosk = true;    

    /* eslint-disable @typescript-eslint/no-unused-vars */
    private _onChangeCallback = (_v:any) => {
        // nothing to do (required by angular)
    };

    private _onTouchCallback = () => {
        // nothing to do (required by angular)
    };
    /* eslint-enable @typescript-eslint/no-unused-vars */

    /** Internal value of the input. */
    private _innerValue: string | null = null;

    /**
     * Constructs an instance of `UppSearchComponent`.
     */        
    constructor() {
        // nothing to do
    }

    /**
     * Lifecycle hook. Initializes the component and configures initial values.
     */       
    ngOnInit() {
        this._innerValue = this.value;
    }
    
    /**
     * Clears the search value
     */      
    OnClear(){
        this.value = '';
    }

    /** Emits the current value when it changes. */
    @Output() Changed = new EventEmitter<string | null>(); 

    /********************************************/
    /* get/set accesors                         */
    /********************************************/
       
    @Input()

    /**
     * Gets the current value of the input.
     * @returns {string | null} The current value of the input.
     */       
    get value(): string | null {
        return this._innerValue;
    }

    /**
     * Sets a new value for the input.
     * Updates the internal value, notifies Angular Forms, and triggers value change events.
     * @param {string | null} v - The new value to set.
     */     
    set value(v: string | null) {
        if (v !== this._innerValue) {
            this._innerValue = v;

            this._onChangeCallback(v);
            this._onTouchCallback();
            
            this.Changed.emit(v);
        }
    }     
    
    /********************************************/
    /* ControlValueAccessor                     */
    /********************************************/
    
    /**
     * Writes a value to the input. Required by `ControlValueAccessor`.
     * @param {string} value - The value to write.
     */      
    writeValue(value: any) {
        if (value !== this._innerValue) {
            this.value = value;
            this.Changed.emit(this.value);
        }
    }

    /**
     * Registers a callback function to be called when the input value changes.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */     
    registerOnChange(fn: any) {
        this._onChangeCallback = fn;
    }

    /**
     * Registers a callback function to be called when the input is touched.
     * Required by `ControlValueAccessor`.
     * @param {Function} fn - The callback function.
     */      
    registerOnTouched(fn: any) {
        this._onTouchCallback = fn;
    }    

    /**
     * Sets the disabled state of the search input.
     * Part of the ControlValueAccessor interface.
     * 
     * @param isDisabled Whether the control should be disabled
     */
    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }    
}
